import type { NavigationMenuItem } from '@nuxt/ui'
import { computed } from 'vue'

import { Role } from '~~/shared/Roles'

type AppNavItem = NavigationMenuItem & {
  allowedRoles?: Role[]
  children?: AppNavItem[]
  allowedEmployeeTypes?: string[]
}

const NAV_ITEMS: AppNavItem[] = [
  {
    label: 'Menu',
    icon: 'i-lucide-calendar-days',
    to: '/menu',
  },
  {
    label: 'Bestil mad',
    icon: 'i-lucide-utensils',
    children: [
      { label: 'Aftensmad',
        to: '/order/dinner',
      },
      { label: 'Takeaway',
        to: '/order/takeaway',
      },
      {
        label: 'Mødeforplejning',
        to: '/order/meeting',
        allowedEmployeeTypes: ['2'],
        allowedRoles: [Role.CanteenAdmin],
      },
    ],
  },
  {
    label: 'Mine bestillinger',
    icon: 'i-lucide-clipboard-list',
    to: '/order/overview',
  },
  {
    label: 'Administration',
    icon: 'i-lucide-shield',
    allowedRoles: [Role.CanteenAdmin],
    children: [
      {
        label: 'Bestillinger',
        to: '/admin/orders',
        allowedRoles: [Role.CanteenAdmin],
      },
      {
        label: 'Statistik',
        to: '/admin/statistics',
        allowedRoles: [Role.CanteenAdmin],
      },
      { label: 'Produkter',
        to: '/admin/products',
        allowedRoles: [Role.CanteenAdmin],
      },
    ],
  },
  {
    label: 'Administrér passwords',
    icon: 'i-lucide-key-round',
    allowedRoles: [Role.PasswordAdmin],
    to: '/admin/passwords',
  },
]

function isRouteActive(target: string, currentPath: string) {
  return currentPath === target || currentPath.startsWith(target + '/')
}

function buildNav(items: AppNavItem[], userRoles: Role[], currentPath: string, employeeType?: string): NavigationMenuItem[] {
  return items
    .map((item) => {
      const hasRole
        = !!item.allowedRoles?.length && item.allowedRoles.some(r => userRoles.includes(r))

      const hasEmployeeType
        = !!item.allowedEmployeeTypes?.length
          && employeeType != null
          && item.allowedEmployeeTypes.includes(employeeType)

      const isUnrestricted
        = !item.allowedRoles?.length && !item.allowedEmployeeTypes?.length

      const allowed = isUnrestricted || hasRole || hasEmployeeType

      if (!allowed) return null

      const children = item.children
        ? buildNav(item.children, userRoles, currentPath, employeeType)
        : undefined

      if (item.children && (!children || children.length === 0)) {
        return null
      }

      const selfActive = item.to
        ? isRouteActive(item.to as string, currentPath)
        : false

      const childActive = children?.some(c => c.active)

      return {
        ...item,
        children,
        active: selfActive || childActive,
      }
    })
    .filter(i => i !== null)
}

export const useNav = () => {
  const { roles, employeeType } = useUser()
  const route = useRoute()

  const items = computed<NavigationMenuItem[]>(() => {
    return buildNav(NAV_ITEMS, roles.value, route.path, employeeType.value)
  })

  return { items }
}
